/**
 * Facebook Pixel Adapter
 *
 * Adapts standardized event data into Facebook Pixel format.
 * Handles both browser pixel (fbq) and CAPI (server-to-server) data structures.
 *
 * @since 1.51.0
 */

(function (pmw, $, undefined) {

	/**
	 * Facebook Pixel Data Adapter
	 */
	const FacebookAdapter = {

		/**
		 * Main adapt method - routes to specific event adapters
		 *
		 * @param {object} eventData - Standardized event data
		 * @param {string} eventName - Event name (add_to_cart, view_item, etc.)
		 * @param {object} context - Event context (user, session, page data)
		 * @returns {Promise<object|null>} Facebook-formatted data or null if pixel inactive
		 */
		async adapt(eventData, eventName, context) {
			// Check if Facebook pixel is configured (has pixel_id)
			// Don't check 'loaded' status - pixel might not be loaded yet when event fires
			// The event listener will check canNotFireFbq() before actually firing
			if (!pmwDataLayer?.pixels?.facebook?.pixel_id) {
				return null;
			}

			// Map standardized event names to adapter methods
			const eventMap = {
				"page_view"       : this.adaptPageView,
				"add_to_cart"     : this.adaptAddToCart,
				"view_item"       : this.adaptViewItem,
				"begin_checkout"  : this.adaptBeginCheckout,
				"add_payment_info": this.adaptAddPaymentInfo,
				"add_to_wishlist" : this.adaptAddToWishlist,
				"search"          : this.adaptSearch,
				"purchase"        : this.adaptPurchase,
				"login"           : this.adaptLogin,
			};
			const adapter  = eventMap[eventName];

			if (!adapter) {
				pmw.console.log(`No Facebook adapter for event: ${eventName}`);
				return null;
			}

			try {
				return await adapter.call(this, eventData, context);
			} catch (e) {
				pmw.console.error(`Facebook adapter error for ${eventName}:`, e);
				return null;
			}
		},

		/**
		 * Get base event data structure
		 * Common fields for all Facebook events
		 * Waits for _fbp cookie on first pageview to ensure proper event matching
		 */
		async getBaseData(context) {
			// Wait for _fbp cookie (max 2 seconds) - important for first pageview
			await pmw.waitForFbp();

			const baseData = {
				event_id        : pmw.getRandomEventId(),
				action_source   : "website",
				event_time      : Math.floor(Date.now() / 1000),
				user_data       : pmw.getFbUserData(),
				event_source_url: context.url,
			};

			// Add referrer if available
			if (context.referrer) {
				baseData.referrer_url = context.referrer;
			}

			return baseData;
		},

		/**
		 * Adapt PageView event
		 */
		async adaptPageView(eventData, context) {
			return {
				event_name: "PageView",
				...await this.getBaseData(context),
				custom_data: {},
			};
		},

		/**
		 * Adapt AddToCart event
		 */
		async adaptAddToCart(eventData, context) {
			const product = eventData.product;

			return {
				event_name: "AddToCart",
				...await this.getBaseData(context),
				custom_data: pmw.fbGetProductDataForCapiEvent(product),
			};
		},

		/**
		 * Adapt ViewContent (view_item) event
		 */
		async adaptViewItem(eventData, context) {
			const product = eventData.product;

			const data = {
				event_name: "ViewContent",
				...await this.getBaseData(context),
				custom_data: {},
			};

			// Add product data if product is provided
			if (product) {
				data.custom_data = pmw.fbGetProductDataForCapiEvent(product);
			}

			return data;
		},

		/**
		 * Adapt InitiateCheckout (begin_checkout) event
		 */
		async adaptBeginCheckout(eventData, context) {
			const data = {
				event_name: "InitiateCheckout",
				...await this.getBaseData(context),
				custom_data: {},
			};

			// Add cart data if available
			if (pmwDataLayer?.cart && !jQuery.isEmptyObject(pmwDataLayer.cart)) {
				data.custom_data = {
					content_type: "product",
					content_ids : pmw.fbGetContentIdsFromCart(),
					value       : pmw.getCartValue(),
					currency    : pmwDataLayer.shop.currency,
				};
			}

			return data;
		},

		/**
		 * Adapt AddPaymentInfo event
		 */
		async adaptAddPaymentInfo(eventData, context) {
			return {
				event_name: "AddPaymentInfo",
				...await this.getBaseData(context),
				custom_data: {},
			};
		},

		/**
		 * Adapt AddToWishlist event
		 */
		async adaptAddToWishlist(eventData, context) {
			const product = eventData.product;

			return {
				event_name: "AddToWishlist",
				...await this.getBaseData(context),
				custom_data: pmw.fbGetProductDataForCapiEvent(product),
			};
		},

		/**
		 * Adapt Search event
		 */
		async adaptSearch(eventData, context) {
			return {
				event_name: "Search",
				...await this.getBaseData(context),
				custom_data: {
					search_string: pmw.getSearchTermFromUrl(),
				},
			};
		},

		/**
		 * Adapt Purchase event
		 */
		async adaptPurchase(eventData, context) {
			if (!pmwDataLayer?.order) {
				pmw.console.error("Order data not available for Facebook Purchase event");
				return null;
			}

			const baseData    = await this.getBaseData(context);
			// Use order ID for purchase event to match server-side event ID
			baseData.event_id = "pmw_" + pmwDataLayer.order.id.toString();

			return {
				event_name: "Purchase",
				...baseData,
				custom_data: {
					value       : pmwDataLayer.order.value.marketing,
					currency    : pmwDataLayer.order.currency,
					content_type: "product",
					content_ids : pmw.facebookContentIds(),
				},
			};
		},

		/**
		 * Adapt Login event
		 *
		 * Note: Facebook doesn't have a standard Login event in their predefined events.
		 * Return null as this event is not typically tracked via Facebook CAPI.
		 *
		 * @param {object} eventData - Event data
		 * @param {object} context - Event context
		 * @returns {null} Facebook doesn't support login event
		 */
		async adaptLogin(eventData, context) {
			// Facebook doesn't have a standard Login event
			return null;
		},
	};

	// Register the adapter with S2S capabilities and 'both' firing mode
	// 'both' mode: fires browser pixel (fbq) AND sends to CAPI, using event_id for deduplication
	pmw.registerAdapter("facebook", FacebookAdapter, {
		mode                      : "both",
		supportsS2S               : true,
		supportsEventDeduplication: true,
		supportsPageViewS2S       : true,
		isActive                  : () => pmwDataLayer?.pixels?.facebook?.loaded === true && pmw.consent.categories.get().marketing === true,
		isConfigured              : () => !!pmwDataLayer?.pixels?.facebook?.pixel_id,
	});

}(window.pmw = window.pmw || {}, jQuery));
